import { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { Landing } from "./ui/Landing.tsx";
import { useUndoRedo } from "./ui/useUndoRedo.ts";
import { useSimulationWorker } from "./ui/useSimulationWorker.ts";
import { useLongitudinalProfile } from "./ui/useLongitudinalProfile.ts";
import { useResultsAnimation } from "./ui/useResultsAnimation.ts";
import { useResultsDashboard } from "./ui/useResultsDashboard.ts";
import { useThematicMap } from "./ui/useThematicMap.ts";
import { useMapQuery } from "./ui/useMapQuery.ts";
import { useNetworkTrace, type TraceDirection } from "./ui/useNetworkTrace.ts";
import { useDialogToggles } from "./ui/useDialogToggles.ts";
import { useMapOverlayColors } from "./ui/useMapOverlayColors.ts";
import { useDeleteFlow } from "./ui/useDeleteFlow.ts";
import { useClipboard } from "./ui/useClipboard.ts";
import { ADD_LINK, ADD_POINT, type EditMode } from "./ui/editModeTools.ts";
import { useKeyboardShortcuts } from "./ui/useKeyboardShortcuts.ts";
import { useProjectStorage } from "./ui/useProjectStorage.ts";
import { useProjectLifecycle } from "./ui/useProjectLifecycle.ts";
import { useProjectUrlSync } from "./ui/useProjectUrlSync.ts";
import { useRawSectionEditor } from "./ui/useRawSectionEditor.ts";
import { useVertexDragHandlers } from "./ui/useVertexDragHandlers.ts";
import { useMapInteraction } from "./ui/useMapInteraction.ts";
import { useMeasureTool } from "./ui/useMeasureTool.ts";
import { useSimulationRun } from "./ui/useSimulationRun.ts";
import { useProjectDataActions } from "./ui/useProjectDataActions.ts";
import { useAutosave } from "./ui/useAutosave.ts";
import { useExclusiveMapOverlays } from "./ui/useExclusiveMapOverlays.ts";
import { useAppPreferences } from "./ui/useAppPreferences.ts";
import { AppBar } from "./ui/AppBar.tsx";
import { ToolRail } from "./ui/ToolRail.tsx";
import { AppDialogs } from "./ui/AppDialogs.tsx";
import { NetworkMap } from "./ui/NetworkMap.tsx";
// Lazily loaded — Leaflet + react-leaflet only ever render for the minority
// of projects with a validated CRS; a static import put the whole
// GeoreferencedMap.tsx module (and Leaflet itself) in the main bundle
// regardless, pushing it over Vite's 500KB warning threshold.
const GeoreferencedMap = lazy(() =>
  import("./ui/GeoreferencedMap.tsx").then((m) => ({ default: m.GeoreferencedMap }))
);
import { ElementBrowser } from "./ui/ElementBrowser.tsx";
import { PropertyPanel } from "./ui/PropertyPanel.tsx";
import { ResultsDashboardPanel } from "./ui/ResultsDashboardPanel.tsx";
import { deriveProject } from "./model/inp/parseInp.ts";
import { readBackdropCalibration } from "./model/inp/backdrop.ts";
import { serializeInp } from "./model/inp/serializeInp.ts";
import { setPropertyValue, type ElementRef } from "./model/inp/properties.ts";
import type { VertexTarget } from "./model/inp/editNetwork.ts";
import { vertexPointCount } from "./model/inp/vertexHandles.ts";
import { downloadTextFile } from "./model/downloadFile.ts";
import { LanguageProvider } from "./i18n/context.tsx";
import { traceNetwork } from "./model/inp/networkTrace.ts";
import { pluralText, translate, type Translate } from "./i18n/hooks.ts";
import { kindLabelKey } from "./i18n/fieldLabels.ts";
import { AlertDialog } from "./ui/AlertDialog.tsx";
import { MultiSelectBar } from "./ui/MultiSelectBar.tsx";
import type { DataTab } from "./ui/ProjectDataDialog.tsx";
import { Logo } from "./ui/Logo.tsx";
import { SpinnerIcon } from "./ui/SpinnerIcon.tsx";
import { SimulationProgressBar } from "./ui/SimulationProgressBar.tsx";
import { ResultsAnimationBar } from "./ui/ResultsAnimationBar.tsx";
import { ThematicMapBar } from "./ui/ThematicMapBar.tsx";
import { MapQueryBar } from "./ui/MapQueryBar.tsx";
import { NetworkTraceBar } from "./ui/NetworkTraceBar.tsx";
import type { StoredProject } from "./model/storage.ts";
import { ExampleBanner } from "./ui/ExampleBanner.tsx";
import { SimulationWarningsBanner } from "./ui/SimulationWarningsBanner.tsx";
import { SAMPLE_PROJECTS } from "./model/sampleProjects.ts";
import { ALL_KINDS } from "./ui/useNetworkMapState.ts";

/** Every element KIND (not category) starts visible — keyed by kind, not `ElementCategory`, since a project can mix a handful of dividers into thousands of junctions and hiding just the crowded kind matters. */
const ALL_KINDS_VISIBLE: Record<string, boolean> = Object.fromEntries(
  ALL_KINDS.map((kind) => [kind, true])
);

export default function App() {
  const [loaded, setLoaded] = useState<StoredProject | null>(null);
  const [selected, setSelected] = useState<ElementRef | null>(null);
  /** Set only when `applyGeoreference` actually drops a row (non-finite input/output coordinate) — same pattern as `storageError` below, own `AlertDialog`. */
  const [georeferenceWarnings, setGeoreferenceWarnings] = useState<string[] | null>(null);
  /** Set only when opening a fresh `.inp`/sample drops one or more malformed link rows — same pattern as `georeferenceWarnings` above, own `AlertDialog`. */
  const [malformedLinkWarnings, setMalformedLinkWarnings] = useState<string[] | null>(null);
  // Bumped only when a selection comes from ElementBrowser/ElementSearch —
  // the map's recenter-on-selection effect keys off THIS, not `selected`
  // itself, so a plain click ON the map (already visible) never re-pans the
  // view. Picking from a list of hundreds of near-identical nodes still
  // needs the recenter.
  const [recenterToken, setRecenterToken] = useState(0);
  // Shift-click adds/removes an element from this set (map only); `selected`
  // above keeps driving the PropertyPanel + "primary" highlight regardless.
  // Copy (Ctrl+C) copies this set when non-empty, else falls back to
  // `selected` alone. Cleared on a plain click, background click, or
  // undo/redo (the underlying elements may no longer exist, or exist under
  // a different row shape).
  const [multiSelected, setMultiSelected] = useState<ElementRef[]>([]);
  // Toolbar toggle standing in for holding Shift — every bulk-select path
  // checks `shiftKey || multiSelectMode`. Persistent across Escape, same
  // treatment as `editGeometry` (a mode switch, not a one-shot action).
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  // Reset whenever a different project's `rawSections` becomes active —
  // history from one project has no meaning applied to another.
  const undoRedo = useUndoRedo();
  const {
    savedProjects,
    storageError,
    setStorageError,
    reportStorageError,
    refreshSavedProjects,
    deleteSavedProject,
  } = useProjectStorage();
  // Whether dragging (a node/vertex/midpoint handle) is allowed right now —
  // separate from `mode`/`selected`: selecting an element always works
  // regardless. Gates only the one thing that can accidentally change the
  // network from a stray drag — found live: with dragging always on, there
  // was no way to inspect an element's properties without risking nudging it.
  const [editGeometry, setEditGeometry] = useState(false);
  // Which vertex the keyboard shortcuts (`[`/`]` cycle, arrows nudge,
  // Insert/+ adds one, Delete removes it) currently act on — the mouse-driven
  // handles have no equivalent state of their own, so this is the only place
  // that concept exists. Not eagerly cleared on selection change: instead
  // `activeFocusedVertex` re-validates it against the CURRENT
  // `selected`/`editGeometry` on every render, so a stale entry is simply
  // never shown/acted on.
  const [focusedVertex, setFocusedVertex] = useState<{
    target: VertexTarget;
    index: number;
  } | null>(null);
  // Per-KIND "shown on the map" toggle (one per element kind, not per
  // category) — a project can mix a handful of one kind into thousands of
  // another, so decluttering by kind matters as much as by category. Not
  // persisted: a fresh project load always starts with everything visible.
  const [visibleKinds, setVisibleKinds] = useState<Record<string, boolean>>(ALL_KINDS_VISIBLE);
  /** Flips `kind`'s visibility when `explicit` is omitted; sets it to exactly `explicit` when given, so a "show/hide every kind under this category" button converges to one uniform state even when some kinds started visible and others didn't. */
  const toggleKindVisibility = useCallback((kind: string, explicit?: boolean) => {
    setVisibleKinds((current) => ({ ...current, [kind]: explicit ?? !current[kind] }));
  }, []);
  const {
    theme,
    toggleTheme,
    language,
    setLanguage,
    sidebarVisible,
    setSidebarVisible,
    elementDefaults,
    setElementDefaults,
  } = useAppPreferences();
  // Plain open/closed flags shared by 8 dialogs/menus (see
  // useDialogToggles.ts). `projectDataTab` (which single-section dialog
  // ProjectDataMenu's picker opened, `null` = none) stays separate since it
  // carries real data, not just whether a dialog is open.
  const dialogs = useDialogToggles();
  const [projectDataTab, setProjectDataTab] = useState<DataTab | null>(null);
  const resultsAnimation = useResultsAnimation();
  // Openable with NO simulation run yet (ground/invert still shows; the
  // water surface line is simply absent until `results` exists).
  const longitudinalProfile = useLongitudinalProfile();
  const resultsDashboard = useResultsDashboard();
  // Colors nodes/links/subcatchments by a STATIC attribute, so (like the
  // profile dialog above) never disabled on `!results`. Mutually exclusive
  // with resultsAnimation.open — opening one closes the other, enforced at
  // each toggle below.
  const thematicMap = useThematicMap();
  const mapQuery = useMapQuery();
  // ToolsMenu's "trace upstream/downstream" state — same map-highlight-
  // overlay family as the three above, mutually exclusive with all of them.
  const networkTrace = useNetworkTrace();
  // For the handful of places in this component itself that need translated
  // text (LanguageProvider only reaches components BELOW it — App.tsx can't
  // consume its own context).
  const t: Translate = useMemo(() => (key, params) => translate(language, key, params), [language]);
  // A fresh real `Worker` per run (production) — `createWorker` is the sole
  // seam a test injects a mock through instead.
  const simulationWorker = useSimulationWorker({
    createWorker: () =>
      new Worker(new URL("./engine/simulateProject.worker.ts", import.meta.url), {
        type: "module",
      }),
    workerCrashedMessage: t("simulation.workerCrashedMessage"),
  });
  // Only meaningful once a project with a CRS is loaded. Reset below
  // whenever a (different) project loads: a georeferenced project opens
  // straight into the map view.
  const [mapMode, setMapMode] = useState<"plane" | "geo">("plane");
  // Off by default — an arrow on every single link is a lot of extra ink on
  // a real network that most of the time isn't what someone opening a
  // project wants to see first.
  const [showLinkDirection, setShowLinkDirection] = useState(false);
  const [showOverview, setShowOverview] = useState(false);
  // Names of elements a GIS import left with at least one column still at
  // its column default, not something the user actually mapped — surfaced
  // as a marker in ElementBrowser so they're easy to find and finish.
  // Session-only (never written to rawSections): reset whenever a different
  // project loads or the current one closes.
  const [placeholderElements, setPlaceholderElements] = useState<Set<string>>(new Set());

  // Re-derived on every raw-section change, not incrementally patched — a
  // SWMM project is small enough that a full re-derive is cheap, and it
  // rules out an entire class of "typed view disagrees with raw data" bugs.
  // Deliberately depends on `loaded.rawSections`, NOT `loaded` itself — a
  // rename/CRS-assign/background-image-swap/etc. replaces `loaded` with a
  // new object but preserves its own `rawSections` reference (every such
  // setter spreads the old `loaded`), so keying on the narrower value avoids
  // re-deriving the WHOLE project on a change this memo was never meant to
  // react to in the first place.
  const project = useMemo(
    () => (loaded ? deriveProject(loaded.rawSections) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `loaded` itself is deliberately excluded, see comment above.
    [loaded?.rawSections]
  );

  // Same "key on rawSections, not loaded" reasoning as `project` above —
  // this was previously called unmemoized on every render.
  const backdropCalibration = useMemo(
    () => (loaded ? readBackdropCalibration(loaded.rawSections) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `loaded` itself is deliberately excluded, see `project`'s own comment above.
    [loaded?.rawSections]
  );

  const {
    mutateSection,
    mutateRawUndoable,
    mutateRawCoalesced,
    commitPendingCoalescedUndo,
    resetPendingCoalesce,
    undo,
    redo,
  } = useRawSectionEditor(
    loaded,
    setLoaded,
    undoRedo,
    simulationWorker.clearResults,
    setSelected,
    setMultiSelected,
    setFocusedVertex
  );

  const { pendingDelete, setPendingDelete, handleDelete } = useDeleteFlow(
    project,
    selected,
    multiSelected,
    t,
    mutateRawUndoable,
    setSelected,
    setMultiSelected
  );
  const {
    clipboard,
    handleCopy,
    handlePaste,
    clear: clearClipboard,
  } = useClipboard(
    loaded,
    project,
    selected,
    multiSelected,
    mutateRawUndoable,
    setSelected,
    setMultiSelected
  );

  const {
    externalFilesMissing,
    warningsDismissed,
    setWarningsDismissed,
    runSimulation,
    supplyExternalFile,
  } = useSimulationRun(loaded, setLoaded, simulationWorker, () =>
    dialogs.openDialog("externalFiles")
  );

  const sampleAttribution = loaded
    ? (SAMPLE_PROJECTS.find((s) => s.fileName === loaded.fileName) ?? null)
    : null;

  // GroupEditDialog only has a common property list to offer when every
  // selected element shares the same category+kind.
  const canGroupEdit =
    multiSelected.length > 1 &&
    multiSelected.every(
      (r) => r.category === multiSelected[0].category && r.kind === multiSelected[0].kind
    );

  // Per-frame color maps, only computed while the overlay is open. Fed to
  // BOTH NetworkMap (plane) and GeoreferencedMap (map) below — found live:
  // GeoreferencedMap used to take no such props at all, so opening the
  // thematic-map/map-query picker on a georeferenced project visibly
  // changed nothing on the map.
  const {
    nodeVariablePeak,
    linkVariablePeak,
    subcatchmentVariablePeak,
    resultsAnimationNodeColors,
    resultsAnimationLinkColors,
    resultsAnimationSubcatchmentColors,
    thematicColorResult,
    thematicNodeColors,
    thematicLinkColors,
    thematicSubcatchmentColors,
    mapQueryAttributeDef,
    mapQueryValues,
    mapQueryMatches,
    mapQueryNodeColors,
    mapQueryLinkColors,
    mapQuerySubcatchmentColors,
    networkTraceNodeColors,
    networkTraceLinkColors,
  } = useMapOverlayColors(
    project,
    simulationWorker.results,
    resultsAnimation,
    thematicMap,
    mapQuery,
    networkTrace
  );

  // Same merge, fed to BOTH NetworkMap (plane) and GeoreferencedMap (map)
  // below — computed once here instead of duplicating the `??` chain at each
  // map's own JSX call site (the two maps render mutually exclusively, so
  // there's only ever one consumer per render anyway).
  const mergedNodeColors =
    networkTraceNodeColors ??
    resultsAnimationNodeColors ??
    thematicNodeColors ??
    mapQueryNodeColors;
  const mergedLinkColors =
    networkTraceLinkColors ??
    resultsAnimationLinkColors ??
    thematicLinkColors ??
    mapQueryLinkColors;
  const mergedSubcatchmentColors =
    resultsAnimationSubcatchmentColors ?? thematicSubcatchmentColors ?? mapQuerySubcatchmentColors;

  useAutosave(loaded, refreshSavedProjects, reportStorageError);

  const measureTool = useMeasureTool();

  const {
    savedProjectNotFound,
    setSavedProjectNotFound,
    openNewFile,
    openSavedProject,
    closeProject,
    renameProject,
    duplicateProject,
    deleteCurrentProject,
  } = useProjectLifecycle({
    loaded,
    setLoaded,
    refreshSavedProjects,
    reportStorageError,
    reportMalformedLinkWarnings: setMalformedLinkWarnings,
    stopSimulation: simulationWorker.stop,
    setSelected,
    setMultiSelected,
    clearClipboard,
    setPlaceholderElements,
    resetUndo: undoRedo.reset,
    resetPendingCoalesce,
    resetLongitudinalProfile: longitudinalProfile.reset,
    resetResultsDashboard: resultsDashboard.reset,
    resetNetworkTrace: networkTrace.reset,
    resetMeasureTool: measureTool.deactivate,
    setMapMode,
    resetVisibleKinds: () => setVisibleKinds(ALL_KINDS_VISIBLE),
  });

  const { restoringFromUrl } = useProjectUrlSync(loaded, openSavedProject, closeProject);

  const projectDataActions = useProjectDataActions({
    loaded,
    setLoaded,
    mutateRawUndoable,
    setPlaceholderElements,
    setMapMode,
    closeGeoreferenceDialog: () => dialogs.close("georeference"),
    reportGeoreferenceWarnings: setGeoreferenceWarnings,
  });

  const {
    mode,
    conduitStart,
    subcatchmentDraft,
    changeMode,
    handleElementClick,
    selectFromList,
    handleBackgroundClick,
    finishSubcatchmentDraft,
    handleAreaSelect,
    handleSelectByType,
    handleSelectMapQueryMatches,
  } = useMapInteraction({
    project,
    loaded,
    elementDefaults,
    selected,
    setSelected,
    multiSelected,
    setMultiSelected,
    multiSelectMode,
    bumpRecenterToken: () => setRecenterToken((token) => token + 1),
    mutateRawUndoable,
    longitudinalProfile,
    networkTrace,
    mapQuery,
    mapQueryMatches,
    mapQueryAttributeDef,
  });

  const {
    toggleResultsAnimation,
    toggleThematicMap,
    toggleMapQuery,
    startNetworkTrace,
    runNetworkTrace,
  } = useExclusiveMapOverlays(resultsAnimation, thematicMap, mapQuery, networkTrace);

  /** Direct-trace variant wired to the PropertyPanel's own contextual
   *  action row for an already-selected node — no pick round trip needed,
   *  unlike the ToolRail "Herramientas" menu's own `startNetworkTrace`
   *  above. Computes the trace with the currently-selected node as the
   *  start; a no-op if somehow called without a node selected. */
  function runNetworkTraceOnSelected(direction: TraceDirection) {
    if (!project || !selected || selected.category !== "node") return;
    runNetworkTrace(selected.name, direction, traceNetwork(project, selected.name, direction));
  }

  // Mutually exclusive with the mode-based edit tools (add-junction,
  // add-conduit...) — both would otherwise race for the same background
  // click. Activating measure resets `mode` back to "select"; picking any
  // OTHER tool turns measure back off.
  function toggleMeasureTool() {
    measureTool.toggle();
    changeMode("select");
  }
  function changeModeAndStopMeasuring(next: EditMode) {
    if (measureTool.active) measureTool.deactivate();
    changeMode(next);
  }

  const selectionShape = mode === "select-rect" ? "rect" : mode === "select-lasso" ? "lasso" : null;

  // `focusedVertex` re-validated against the CURRENT selection/mode (a
  // derived value rather than something eagerly cleared on every place
  // `selected` could change).
  const activeFocusedVertex =
    editGeometry &&
    mode === "select" &&
    selected &&
    (selected.category === "link" || selected.category === "subcatchment") &&
    focusedVertex &&
    focusedVertex.target.name === selected.name
      ? focusedVertex
      : null;
  const focusedVertexCount = project && selected ? vertexPointCount(project, selected) : 0;

  const { handleMoveNode, handleMoveVertex, handleInsertVertex, handleDeleteVertex } =
    useVertexDragHandlers(mutateRawUndoable);

  useKeyboardShortcuts({
    selected,
    multiSelected,
    clipboard,
    pendingDelete,
    dialogsOpen: dialogs.open,
    project,
    loaded,
    undoStack: undoRedo.undoStack,
    redoStack: undoRedo.redoStack,
    editGeometry,
    mode,
    activeFocusedVertex,
    setFocusedVertex,
    mutateRawUndoable,
    changeMode,
    undo,
    redo,
    handleDelete,
    handleCopy,
    handlePaste,
    setSelected,
    setMultiSelected,
  });

  function handlePropertyChange(
    section: string,
    elementName: string,
    columnIndex: number,
    value: string
  ) {
    mutateRawCoalesced((raw) => setPropertyValue(raw, section, elementName, columnIndex, value));
  }

  if (restoringFromUrl) {
    return (
      <LanguageProvider language={language}>
        <div className="landing">
          <div className="landing-card">
            <div className="landing-logo">
              <Logo size={64} />
            </div>
            <SpinnerIcon />
          </div>
        </div>
      </LanguageProvider>
    );
  }

  if (!loaded || !project) {
    return (
      <LanguageProvider language={language}>
        {savedProjectNotFound && (
          <AlertDialog
            title={t("landing.savedProjectNotFoundTitle")}
            message={t("landing.savedProjectNotFoundMessage")}
            onClose={() => setSavedProjectNotFound(false)}
          />
        )}
        <Landing
          savedProjects={savedProjects}
          onOpen={(rawSections, comments, fileName, crs, name) =>
            void openNewFile(rawSections, comments, fileName, crs, name)
          }
          onOpenSample={(rawSections, comments, fileName, crs, name) =>
            void openNewFile(rawSections, comments, fileName, crs, name, true)
          }
          onOpenSaved={(id) => void openSavedProject(id)}
          onDeleteSaved={(id) => void deleteSavedProject(id)}
          theme={theme}
          onToggleTheme={toggleTheme}
          language={language}
          onChangeLanguage={setLanguage}
          settingsMenuOpen={dialogs.open.settingsMenu}
          onToggleSettingsMenu={() => dialogs.toggle("settingsMenu")}
          onCloseSettingsMenu={() => dialogs.close("settingsMenu")}
        />
      </LanguageProvider>
    );
  }

  function exportInp() {
    if (!loaded) return;
    downloadTextFile(serializeInp(loaded.rawSections, loaded.comments), loaded.fileName);
  }

  return (
    <LanguageProvider language={language}>
      <div className="workspace">
        <AppBar
          loaded={loaded}
          project={project}
          t={t}
          dialogs={dialogs}
          onRenameProject={renameProject}
          onDuplicateProject={duplicateProject}
          onExportInp={exportInp}
          onDeleteProject={deleteCurrentProject}
          onCloseProject={closeProject}
          mapMode={mapMode}
          setMapMode={setMapMode}
          showOverview={showOverview}
          setShowOverview={setShowOverview}
          showLinkDirection={showLinkDirection}
          setShowLinkDirection={setShowLinkDirection}
          sidebarVisible={sidebarVisible}
          setSidebarVisible={setSidebarVisible}
          onSelectFromList={selectFromList}
          canUndo={undoRedo.canUndo}
          onUndo={undo}
          canRedo={undoRedo.canRedo}
          onRedo={redo}
          onRunOrCancelSimulation={
            simulationWorker.simulating ? simulationWorker.cancel : runSimulation
          }
          simulationWorker={simulationWorker}
          resultsDashboard={resultsDashboard}
          resultsAnimationOpen={resultsAnimation.open}
          onToggleResultsAnimation={toggleResultsAnimation}
          theme={theme}
          onToggleTheme={toggleTheme}
          language={language}
          onChangeLanguage={setLanguage}
        />
        <div className="workspace-body">
          {/* UI redesign phase 4 — "estado" slot: floats over the map/sidebar
              area instead of pushing it down a full-width strip at a time
              (the pre-redesign layout). SimulationProgressBar shows only
              while `simulating`, SimulationWarningsBanner only once
              `results` exists, and those two are mutually exclusive (a
              worker's `results` are only set once it stops simulating) —
              so at most one of them is ever up at once, no dedicated
              "N more" overflow to build here. ExampleBanner used to live
              here too — moved out (second-audit live feedback: it
              collided with the map-hint banners sharing this same
              top-center real estate) to its own corner slot next to the
              map's own zoom controls, see its own header. */}
          <div className="state-banners">
            <SimulationProgressBar progress={simulationWorker.simulationProgress} />
            {simulationWorker.results && !warningsDismissed && (
              <SimulationWarningsBanner
                warnings={simulationWorker.results.warnings}
                onDismiss={() => setWarningsDismissed(true)}
              />
            )}
          </div>
          <ToolRail
            t={t}
            dialogs={dialogs}
            mode={mode}
            editGeometry={editGeometry}
            setEditGeometry={setEditGeometry}
            onChangeMode={changeModeAndStopMeasuring}
            multiSelectMode={multiSelectMode}
            setMultiSelectMode={setMultiSelectMode}
            onSelectByType={handleSelectByType}
            onOpenQuerySelect={toggleMapQuery}
            mapMode={mapMode}
            measuring={measureTool.active}
            onToggleMeasure={toggleMeasureTool}
            setProjectDataTab={setProjectDataTab}
            onOpenLongitudinalProfile={() => longitudinalProfile.openWithDefaults(project.nodes)}
            thematicMap={thematicMap}
            onToggleThematicMap={toggleThematicMap}
            mapQuery={mapQuery}
            onStartTrace={startNetworkTrace}
          />
          {sidebarVisible && (
            <ElementBrowser
              project={project}
              selected={selected}
              multiSelected={multiSelected}
              onSelect={selectFromList}
              visibleKinds={visibleKinds}
              onToggleKindVisibility={toggleKindVisibility}
              placeholderNames={placeholderElements}
            />
          )}
          {/* The workspace's primary content — map + property/results panel
              — as a `<main>` landmark, separate from ElementBrowser's own
              `<nav>` sibling above (deep-review Accessibility #4: the app had
              no navigable main-content region at all). */}
          <main className="workspace-main">
            <div className="map-area">
              {mapMode === "geo" && loaded.crs ? (
                <Suspense
                  fallback={
                    <div className="map-area-loading">
                      <SpinnerIcon />
                    </div>
                  }
                >
                  <GeoreferencedMap
                    key={loaded.id}
                    project={project}
                    selected={selected}
                    multiSelected={multiSelected}
                    onSelect={handleElementClick}
                    recenterToken={recenterToken}
                    allowDrag={mode === "select" && editGeometry}
                    onMoveNode={handleMoveNode}
                    onBackgroundClick={handleBackgroundClick}
                    onMoveVertex={handleMoveVertex}
                    onInsertVertex={handleInsertVertex}
                    onDeleteVertex={handleDeleteVertex}
                    focusedVertex={activeFocusedVertex}
                    crs={loaded.crs}
                    visibleKinds={visibleKinds}
                    drawingPreview={subcatchmentDraft}
                    selectionShape={selectionShape}
                    onAreaSelect={handleAreaSelect}
                    showLinkDirection={showLinkDirection}
                    showOverview={showOverview}
                    nodeColors={mergedNodeColors}
                    linkColors={mergedLinkColors}
                    subcatchmentColors={mergedSubcatchmentColors}
                    measuring={measureTool.active}
                    measurePoints={measureTool.points}
                    measureSegmentsMeters={measureTool.segmentsMeters}
                    measureTotalMeters={measureTool.totalMeters}
                    onMeasureAddPoint={measureTool.addPoint}
                    onMeasureClear={measureTool.clear}
                  />
                </Suspense>
              ) : (
                <NetworkMap
                  key={loaded.id}
                  project={project}
                  selected={selected}
                  multiSelected={multiSelected}
                  onSelect={handleElementClick}
                  recenterToken={recenterToken}
                  allowDrag={mode === "select" && editGeometry}
                  onMoveNode={handleMoveNode}
                  onBackgroundClick={handleBackgroundClick}
                  onMoveVertex={handleMoveVertex}
                  onInsertVertex={handleInsertVertex}
                  onDeleteVertex={handleDeleteVertex}
                  focusedVertex={activeFocusedVertex}
                  visibleKinds={visibleKinds}
                  drawingPreview={subcatchmentDraft}
                  selectionShape={selectionShape}
                  onAreaSelect={handleAreaSelect}
                  crs={loaded.crs}
                  showLinkDirection={showLinkDirection}
                  showOverview={showOverview}
                  nodeColors={mergedNodeColors}
                  linkColors={mergedLinkColors}
                  subcatchmentColors={mergedSubcatchmentColors}
                  backgroundImageBlob={loaded.backgroundImageBlob}
                  backdropCalibration={backdropCalibration}
                />
              )}
              {loaded.ephemeral && (
                <ExampleBanner
                  projectName={loaded.name}
                  onSaveCopy={duplicateProject}
                  attribution={
                    sampleAttribution?.attributionKey
                      ? t(sampleAttribution.attributionKey)
                      : undefined
                  }
                  sourceUrl={sampleAttribution?.sourceUrl}
                  license={sampleAttribution?.license}
                />
              )}
              {resultsAnimation.open && simulationWorker.results && (
                <ResultsAnimationBar
                  times={simulationWorker.results.times}
                  index={resultsAnimation.index}
                  onIndexChange={resultsAnimation.setIndex}
                  playing={resultsAnimation.playing}
                  onTogglePlay={() => resultsAnimation.setPlaying((playing) => !playing)}
                  onClose={resultsAnimation.close}
                  nodeCount={project.nodes.length}
                  linkCount={project.links.length}
                  subcatchmentCount={project.subcatchments.length}
                  nodeVariablePeak={nodeVariablePeak}
                  linkVariablePeak={linkVariablePeak}
                  subcatchmentVariablePeak={subcatchmentVariablePeak}
                  flowUnits={project.flowUnits}
                  showNodes={resultsAnimation.showNodes}
                  onShowNodesChange={resultsAnimation.setShowNodes}
                  showLinks={resultsAnimation.showLinks}
                  onShowLinksChange={resultsAnimation.setShowLinks}
                  showSubcatchments={resultsAnimation.showSubcatchments}
                  onShowSubcatchmentsChange={resultsAnimation.setShowSubcatchments}
                  nodeVariable={resultsAnimation.nodeVariable}
                  onNodeVariableChange={resultsAnimation.setNodeVariable}
                  linkVariable={resultsAnimation.linkVariable}
                  onLinkVariableChange={resultsAnimation.setLinkVariable}
                  subcatchmentVariable={resultsAnimation.subcatchmentVariable}
                  onSubcatchmentVariableChange={resultsAnimation.setSubcatchmentVariable}
                />
              )}
              {thematicMap.open && (
                <ThematicMapBar
                  attributeKey={thematicMap.attribute}
                  onAttributeChange={thematicMap.setAttribute}
                  min={thematicColorResult?.min ?? 0}
                  max={thematicColorResult?.max ?? 0}
                  onClose={thematicMap.close}
                  hasResults={!!simulationWorker.results}
                  flowUnits={project.flowUnits}
                />
              )}
              {mapQuery.open && (
                <MapQueryBar
                  attributeKey={mapQuery.attribute}
                  onAttributeChange={mapQuery.setAttribute}
                  comparator={mapQuery.comparator}
                  onComparatorChange={mapQuery.setComparator}
                  thresholdInput={mapQuery.thresholdInput}
                  onThresholdChange={mapQuery.setThresholdInput}
                  matchCount={mapQueryMatches?.size ?? 0}
                  totalCount={mapQueryValues?.size ?? 0}
                  onSelectMatches={handleSelectMapQueryMatches}
                  onClose={mapQuery.close}
                  flowUnits={project.flowUnits}
                />
              )}
              {networkTrace.result && (
                <NetworkTraceBar
                  direction={networkTrace.result.direction}
                  startNode={networkTrace.result.startNode}
                  nodeCount={networkTrace.result.nodes.length}
                  linkCount={networkTrace.result.links.length}
                  onClose={networkTrace.clear}
                />
              )}
              {multiSelected.length > 1 &&
                !resultsAnimation.open &&
                !thematicMap.open &&
                !mapQuery.open &&
                !resultsDashboard.open && (
                  <MultiSelectBar
                    count={multiSelected.length}
                    canGroupEdit={canGroupEdit}
                    onGroupEdit={() => dialogs.openDialog("groupEdit")}
                    onDelete={handleDelete}
                    onClose={() => setMultiSelected([])}
                  />
                )}
              {/* Editing-mode instructions ("click the map to place a
                junction"...) — a banner over the map itself, not squeezed
                into the topbar's own toolbar (found live: competing with
                the mode-toolbar buttons for space there wrapped the whole
                topbar to two lines). */}
              {ADD_LINK[mode] &&
                (() => {
                  const kindLabel = t(kindLabelKey(ADD_LINK[mode]!.kind) ?? "toolbar.addElement");
                  return (
                    <div className="map-hint-banner">
                      <span>
                        {conduitStart
                          ? t("toolbar.hintAddLinkWaiting", { kind: kindLabel, name: conduitStart })
                          : t("toolbar.hintAddLinkStart", { kind: kindLabel })}
                      </span>
                    </div>
                  );
                })()}
              {ADD_POINT[mode] && (
                <div className="map-hint-banner">
                  <span>
                    {t("toolbar.hintAddNode", {
                      kind: t(kindLabelKey(ADD_POINT[mode]!.kind) ?? "toolbar.addElement"),
                    })}
                  </span>
                </div>
              )}
              {mode === "add-subcatchment" && (
                <div className="map-hint-banner">
                  <span>
                    {pluralText(
                      t,
                      subcatchmentDraft.length,
                      "toolbar.hintAddSubcatchmentOne",
                      "toolbar.hintAddSubcatchment"
                    )}
                  </span>
                  <button
                    className="button button-primary"
                    disabled={subcatchmentDraft.length < 3}
                    onClick={finishSubcatchmentDraft}
                  >
                    {t("toolbar.finishSubcatchment")}
                  </button>
                  <button className="button" onClick={() => changeMode("select")}>
                    {t("common.cancel")}
                  </button>
                </div>
              )}
              {longitudinalProfile.picking && (
                <div className="map-hint-banner">
                  <span>
                    {t(
                      longitudinalProfile.picking === "start"
                        ? "longitudinalProfile.hintPickStart"
                        : "longitudinalProfile.hintPickEnd"
                    )}
                  </span>
                  <button className="button" onClick={longitudinalProfile.cancelPick}>
                    {t("common.cancel")}
                  </button>
                </div>
              )}
              {networkTrace.picking && (
                <div className="map-hint-banner">
                  <span>
                    {t(
                      networkTrace.picking === "downstream"
                        ? "networkTrace.hintPickDownstream"
                        : "networkTrace.hintPickUpstream"
                    )}
                  </span>
                  <button className="button" onClick={networkTrace.cancelPick}>
                    {t("common.cancel")}
                  </button>
                </div>
              )}
              {editGeometry &&
                mode === "select" &&
                selected &&
                (selected.category === "link" || selected.category === "subcatchment") && (
                  <div className="map-hint-banner">
                    <span>
                      {activeFocusedVertex
                        ? t("toolbar.hintVertexFocused", {
                            index: activeFocusedVertex.index + 1,
                            count: focusedVertexCount,
                          })
                        : t("toolbar.hintVertexIdle")}
                    </span>
                  </div>
                )}
            </div>
            {resultsDashboard.open && simulationWorker.results ? (
              <ResultsDashboardPanel
                results={simulationWorker.results}
                elements={resultsDashboard.elements}
                selected={selected}
                multiSelected={multiSelected}
                flowUnits={project.flowUnits}
                layout={resultsDashboard.layout}
                view={resultsDashboard.view}
                onAddSelection={() =>
                  resultsDashboard.addElements(
                    (multiSelected.length > 0 ? multiSelected : selected ? [selected] : []).filter(
                      (ref) => ref.category !== "raingage" // no simulation output of its own, resultsVariables.ts's own header
                    )
                  )
                }
                onRemoveElement={resultsDashboard.removeElement}
                onClear={resultsDashboard.clear}
                onLayoutChange={resultsDashboard.setLayout}
                onViewChange={resultsDashboard.setView}
                onClose={resultsDashboard.close}
              />
            ) : (
              selected && (
                <PropertyPanel
                  // Forces a full remount on every element switch — without
                  // this, React reuses the same field DOM nodes across two
                  // elements of the same kind (same `group.section` keys), so
                  // an uncommitted, still-invalid local text buffer (e.g. a
                  // PropertyNumberInput mid-edit on "0.") can survive into the
                  // newly-selected element if its own committed value happens
                  // to coincide (e.g. also "0"), since the value prop wouldn't
                  // have visibly changed. Same pattern as ControlRulesEditor's
                  // own `RuleDetail key={selected.name}`.
                  key={`${selected.category}-${selected.kind}-${selected.name}`}
                  project={project}
                  selected={selected}
                  comments={loaded.comments}
                  onClose={() => setSelected(null)}
                  onChange={handlePropertyChange}
                  mutateSection={mutateSection}
                  onBlur={commitPendingCoalescedUndo}
                  results={simulationWorker.results}
                  onRunNetworkTrace={runNetworkTraceOnSelected}
                />
              )
            )}
          </main>
        </div>
      </div>
      <AppDialogs
        loaded={loaded}
        project={project}
        t={t}
        dialogs={dialogs}
        pendingDelete={pendingDelete}
        onClosePendingDelete={() => setPendingDelete(null)}
        simulationWorker={simulationWorker}
        storageError={storageError}
        onCloseStorageError={() => setStorageError(null)}
        georeferenceWarnings={georeferenceWarnings}
        onCloseGeoreferenceWarnings={() => setGeoreferenceWarnings(null)}
        malformedLinkWarnings={malformedLinkWarnings}
        onCloseMalformedLinkWarnings={() => setMalformedLinkWarnings(null)}
        externalFilesMissing={externalFilesMissing}
        onSupplyExternalFile={supplyExternalFile}
        projectDataActions={projectDataActions}
        projectIsEmpty={
          project.nodes.length === 0 &&
          project.links.length === 0 &&
          project.subcatchments.length === 0 &&
          project.raingages.length === 0
        }
        longitudinalProfile={longitudinalProfile}
        elementDefaults={elementDefaults}
        onSaveElementDefaults={setElementDefaults}
        canGroupEdit={canGroupEdit}
        multiSelected={multiSelected}
        mutateRawUndoable={mutateRawUndoable}
        projectDataTab={projectDataTab}
        setProjectDataTab={setProjectDataTab}
      />
    </LanguageProvider>
  );
}
